<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { repair as repairPage } from '@/routes/tools/pdf';
import repairTool from '@/routes/tools/pdf/repair';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.repair.seo',
    fallbackTitleKey: 'tools.pdf.repair.title',
    fallbackTitleLabel: 'Repair PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: repairPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.repair.title',
                title: 'Repair PDF',
                href: repairPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const status = ref<Status>('idle');
const statusMessage = ref('');

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.repair_failed', 'Repair failed.')
            );
        } catch {
            return t('tools.pdf.ui.repair_failed', 'Repair failed.');
        }
    }

    return t('tools.pdf.ui.repair_failed', 'Repair failed.');
}

async function repair(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.repairing', 'Repairing PDF...');

    const formData = new FormData();
    formData.append('file', file.value);

    try {
        const response = await axios.post(repairTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'repaired.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.repaired_success',
            'PDF repaired successfully!',
        );
        file.value = null;
    } catch (err: unknown) {
        status.value = 'error';
        const msg = await extractError(err);
        statusMessage.value = msg.includes('GhostScript')
            ? t(
                  'tools.pdf.ui.ghostscript_missing_long',
                  'GhostScript is not installed on the server. This tool requires GhostScript.',
              )
            : msg;
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-repair" />

    <ToolLayout
        :title="t('tools.pdf.repair.title', 'Repair PDF')"
        :subtitle="t('tools.pdf.repair.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t(
                            'tools.pdf.ui.drop_pdf_here',
                            'Click or drag PDF here',
                        )
                    "
                    :sublabel="
                        lineWithMax(
                            t('tools.pdf.ui.pdf_files_only', 'PDF files only'),
                        )
                    "
                    @update:model-value="onFileSelected"
                />

                <div v-if="file" class="mt-4">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="removeFile"
                    />
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="repair"
            >
                {{ t('tools.pdf.ui.repair_pdf_button', 'Repair PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
